import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import {
  checkOdbcCredentialsApi,
  CheckOdbcCredentialsPayload,
  CheckOdbcCredentialsResponse,
} from 'IntegrationModule/api/checkOdbcCredentialsApi';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';

type CheckOdbcCredentialsParams = CheckOdbcCredentialsPayload['credentials'];

const ODBC_CHECK_QUERY = 'SELECT TOP(1) tDate from AxTransaction';

type UseCheckOdbcCredentialsMutationOptions = {
  onSuccess?: () => void;
};

export const useCheckOdbcCredentialsMutation = (
  options?: UseCheckOdbcCredentialsMutationOptions,
) => {
  const token = useSelector(selectAuthToken) as string | undefined;
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<CheckOdbcCredentialsResponse>,
    unknown,
    CheckOdbcCredentialsParams
  >({
    mutationFn: async (credentials) => {
      if (!token) throw new Error('Not Authenticated');
      return checkOdbcCredentialsApi({
        params: {
          credentials,
          query: ODBC_CHECK_QUERY,
        },
        token,
      });
    },
    onSuccess: () => {
      showSuccess('ODBC credentials are valid');
      options?.onSuccess?.();
    },
    onError: (error) => {
      const status = (error as { status?: number })?.status;
      if (status === 400) {
        showError('ODBC credentials are invalid');
      } else {
        showError('Failed to check ODBC credentials');
      }
    },
  });

  return {
    checkOdbcCredentials: mutation.mutate,
    checkOdbcCredentialsAsync: mutation.mutateAsync,
    isCheckingOdbcCredentials: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    reset: mutation.reset,
  };
};

export default useCheckOdbcCredentialsMutation;
